import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  ExternalLink,
  FileCheck2,
  FileText,
  Loader2,
  PlayCircle,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

import {
  getMateriaisAdminControllerContarQueryKey,
  getMateriaisAdminControllerListarQueryKey,
  useMateriaisAdminControllerAprovar,
  useMateriaisAdminControllerContar,
  useMateriaisAdminControllerListar,
  useMateriaisAdminControllerRejeitar,
} from '@/api/generated/admin-materiais/admin-materiais';
import type { MaterialAdminResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Paginacao } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

type Filtro = 'PENDENTE' | 'APROVADO' | 'REJEITADO' | 'TODOS';

const ABAS: readonly { valor: Filtro; rotulo: string }[] = [
  { valor: 'PENDENTE', rotulo: 'Pendentes' },
  { valor: 'APROVADO', rotulo: 'Aprovados' },
  { valor: 'REJEITADO', rotulo: 'Rejeitados' },
  { valor: 'TODOS', rotulo: 'Todos' },
];

/**
 * Fila de aprovação de materiais (B8-7).
 *
 * ⚠️ A prévia do vídeo é embutida na própria fila. Mandar o admin abrir o link
 * em outra aba é o caminho mais curto para aprovar no olho — e o que ele está
 * liberando aparece para todos os veterinários da plataforma.
 */
export function AprovacaoMateriaisPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [filtro, setFiltro] = useState<Filtro>('PENDENTE');
  const [pagina, setPagina] = useState(1);
  const [paraRejeitar, setParaRejeitar] = useState<MaterialAdminResponse | undefined>();

  const { data, isPending, isError, refetch } = useMateriaisAdminControllerListar({
    pagina,
    porPagina: 10,
    ordenarPor: 'criadoEm',
    ordem: 'desc',
    ...(filtro !== 'TODOS' ? { status: filtro } : {}),
  });

  const { data: contagem } = useMateriaisAdminControllerContar();
  const aprovar = useMateriaisAdminControllerAprovar();

  const podeDecidir = pode('admin_materiais.editar');

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: getMateriaisAdminControllerListarQueryKey() });
    void queryClient.invalidateQueries({ queryKey: getMateriaisAdminControllerContarQueryKey() });
  }

  if (isError) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <FileCheck2 className="w-7 h-7 text-brand" />
          Aprovação de materiais
        </h2>
        <p className="text-muted-foreground mt-1">
          Revise o que os parceiros enviaram antes de publicar na vitrine.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {ABAS.map((aba) => (
          <button
            key={aba.valor}
            type="button"
            onClick={() => {
              setFiltro(aba.valor);
              setPagina(1);
            }}
            aria-pressed={filtro === aba.valor}
            className={cn(
              'h-8 px-3 rounded-lg text-xs font-medium border transition-colors inline-flex items-center gap-1.5',
              filtro === aba.valor
                ? 'bg-brand text-brand-foreground border-brand'
                : 'bg-transparent text-foreground border-border hover:bg-muted',
            )}
          >
            {aba.rotulo}
            {aba.valor === 'PENDENTE' && (contagem?.pendentes ?? 0) > 0 && (
              <span className="px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                {contagem?.pendentes}
              </span>
            )}
          </button>
        ))}
      </div>

      {isPending ? (
        <div className="space-y-4">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-64 w-full rounded-xl" />
          ))}
        </div>
      ) : data.itens.length === 0 ? (
        <EstadoVazio
          icone={FileCheck2}
          titulo="Fila vazia"
          descricao={
            filtro === 'PENDENTE'
              ? 'Nenhum material aguardando revisão.'
              : 'Nada neste status.'
          }
        />
      ) : (
        <>
          <div className="space-y-4">
            {data.itens.map((material) => (
              <Card key={material.id} className="border-border">
                <CardContent className="p-5 space-y-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                        {material.tipo === 'VIDEO' ? (
                          <PlayCircle className="w-5 h-5 text-brand" />
                        ) : (
                          <FileText className="w-5 h-5 text-brand" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-foreground">{material.titulo}</h3>
                        <p className="text-sm text-brand font-medium">{material.parceiroNome}</p>
                        {material.descricao && (
                          <p className="text-sm text-muted-foreground mt-2">{material.descricao}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          Enviado por {material.autorNome ?? 'desconhecido'} em{' '}
                          {new Date(material.criadoEm).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>

                    <Selo status={material.status} />
                  </div>

                  {material.tipo === 'VIDEO' && material.url && (
                    <div className="aspect-video rounded-lg overflow-hidden border border-border bg-black max-w-xl">
                      <iframe
                        src={material.url}
                        title={material.titulo}
                        className="w-full h-full"
                        allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}

                  {material.tipo === 'PDF' && material.url && (
                    <a
                      href={material.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted hover:bg-muted/70 text-sm"
                    >
                      <FileText className="w-4 h-4 text-brand" />
                      Abrir PDF para revisar
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}

                  {material.status === 'REJEITADO' && material.motivoRejeicao && (
                    <p className="text-xs text-destructive p-2 rounded border border-destructive/30 bg-destructive/5">
                      <strong>Motivo:</strong> {material.motivoRejeicao}
                      {material.avaliadorNome && ` — por ${material.avaliadorNome}`}
                    </p>
                  )}

                  {material.status === 'PENDENTE' && podeDecidir && (
                    <div className="flex justify-end gap-2 pt-3 border-t border-border">
                      <Button
                        variant="outline"
                        className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/5"
                        onClick={() => setParaRejeitar(material)}
                      >
                        <XCircle className="w-4 h-4" />
                        Rejeitar
                      </Button>
                      <Button
                        className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={aprovar.isPending}
                        onClick={() =>
                          aprovar.mutate(
                            { id: material.id },
                            {
                              onSuccess: () => {
                                invalidar();
                                toast.success('Material publicado na vitrine');
                              },
                              onError: (erro) =>
                                toast.error(
                                  erro instanceof ApiError ? erro.message : 'Não foi possível aprovar',
                                ),
                            },
                          )
                        }
                      >
                        {aprovar.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4" />
                        )}
                        Aprovar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Paginacao
            pagina={data.pagina}
            totalPaginas={data.totalPaginas}
            total={data.total}
            porPagina={data.porPagina}
            aoMudarPagina={setPagina}
          />
        </>
      )}

      {paraRejeitar && (
        <RejeicaoDialogo
          material={paraRejeitar}
          aoFechar={() => setParaRejeitar(undefined)}
          aoConcluir={invalidar}
        />
      )}
    </div>
  );
}

function Selo({ status }: { status: string }) {
  if (status === 'APROVADO') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
        Publicado
      </Badge>
    );
  }
  if (status === 'REJEITADO') {
    return <Badge variant="secondary">Rejeitado</Badge>;
  }
  return (
    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
      Pendente
    </Badge>
  );
}

/**
 * ⚠️ O motivo é obrigatório e tem mínimo de 10 caracteres no servidor.
 *
 * Não é burocracia: é o texto que o parceiro lê e com o qual ele corrige. Um
 * "não" mudo devolve o trabalho para o suporte.
 */
function RejeicaoDialogo({
  material,
  aoFechar,
  aoConcluir,
}: {
  material: MaterialAdminResponse;
  aoFechar: () => void;
  aoConcluir: () => void;
}) {
  const rejeitar = useMateriaisAdminControllerRejeitar();
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | undefined>();

  return (
    <Dialog open onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rejeitar material</DialogTitle>
          <DialogDescription>
            &quot;{material.titulo}&quot;, de {material.parceiroNome}. O parceiro vai ler o motivo
            e poderá corrigir e reenviar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Motivo</Label>
          <Textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={4}
            placeholder="Ex.: o vídeo cita indicação fora da bula para a espécie."
          />
          {erro && <p className="text-xs text-destructive">{erro}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar} disabled={rejeitar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={rejeitar.isPending || motivo.trim().length < 10}
            onClick={() =>
              rejeitar.mutate(
                { id: material.id, data: { motivo: motivo.trim() } },
                {
                  onSuccess: () => {
                    aoConcluir();
                    toast.success('Material rejeitado');
                    aoFechar();
                  },
                  onError: (falha) => {
                    if (falha instanceof ApiError && falha.detalhes?.motivo) {
                      setErro(falha.detalhes.motivo[0]);
                      return;
                    }
                    toast.error(
                      falha instanceof ApiError ? falha.message : 'Não foi possível rejeitar',
                    );
                  },
                },
              )
            }
          >
            {rejeitar.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Rejeitar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
